// The documentation tree, in one place — the portal's replacement for
// curri-docs' `sidebars.config.mjs` plus the per-file frontmatter it read.
//
// docs.curri.com redirects here since INT-670; content/README.md is the editing
// guide for these pages.
//
// Two things changed in the move. Titles are declared here rather than parsed
// out of frontmatter, so the nav has one typed source and the renderer needs no
// YAML parser. And `unlisted` is a field rather than a Docusaurus convention:
// 21 of these pages carry `unlisted: true` on purpose, and that flag now does
// double duty — it also carries whole trees (Eclipse, DSP, SSO) that are reachable
// by URL but deliberately absent from the nav.
//
// Numeric filename prefixes are gone; ordering is the array order here.

export type DocTreeId = 'graphql' | 'webhooks' | 'eclipse' | 'dsp' | 'sso'

export interface DocPage {
  /** Path under the tree's `basePath`. Empty string is the tree index. */
  slug: string
  title: string
  /** Reachable by URL, absent from the nav tree. */
  unlisted: boolean
  /**
   * Suppresses the rendered page title, from `hide_title: true` in the old
   * frontmatter.
   *
   * Docusaurus rendered the frontmatter title as the page's `h1`, and 32 of these
   * pages have no `h1` of their own because of it — they would render untitled if
   * this were simply dropped. The ten pages that set the flag open with their own
   * heading and used it to avoid printing the title twice.
   */
  hideTitle: boolean
  /** Path under `content/`, used to look up the raw markdown. */
  file: string
}

export interface DocGroup {
  label: string
  pages: DocPage[]
}

export interface DocTree {
  id: DocTreeId
  label: string
  basePath: string
  groups: DocGroup[]
}

interface PageOptions {
  /** Overrides the slug derived from `file`. Only the tree index needs this. */
  slug?: string
  unlisted?: boolean
  hideTitle?: boolean
}

// Slug defaults to the file path minus its tree directory and extension, which
// is what makes the docs.curri.com redirects a handful of prefix rules rather
// than 43 individual ones.
const page = (
  file: string,
  title: string,
  options: PageOptions = {}
): DocPage => ({
  slug: options.slug ?? file.replace(/^[^/]+\//, '').replace(/\.md$/, ''),
  title,
  unlisted: options.unlisted ?? false,
  hideTitle: options.hideTitle ?? false,
  file,
})

const GRAPHQL: DocTree = {
  id: 'graphql',
  label: 'GraphQL',
  basePath: '/docs/graphql',
  groups: [
    {
      label: 'API',
      pages: [
        // `slug: /` in the old frontmatter — this was docs.curri.com/docs.
        page('graphql/getting-started/welcome.md', 'Welcome', { slug: '', hideTitle: true }),
        page('graphql/getting-started/getting-started.md', 'Getting Started', {
          slug: 'getting-started',
        }),
        page('graphql/getting-started/graphql.md', 'Using GraphQL'),
        page('graphql/getting-started/tutorial.md', 'Tutorial'),
        page('graphql/getting-started/examples.md', 'Examples'),
        page(
          'graphql/getting-started/oauth-strategy.md',
          'Authorizing API access with OAuth 2.0',
          { unlisted: true }
        ),
      ],
    },
    {
      label: 'Queries & Mutations',
      pages: [
        page('graphql/queries-and-mutations/get-a-quote.md', 'Get a Quote'),
        page(
          'graphql/queries-and-mutations/book-a-delivery.md',
          'Book a Delivery'
        ),
        page(
          'graphql/queries-and-mutations/update-a-delivery.md',
          'Update a Delivery'
        ),
        page(
          'graphql/queries-and-mutations/cancel-a-delivery.md',
          'Cancel a Delivery'
        ),
        page('graphql/queries-and-mutations/get-a-delivery.md', 'Get a Delivery'),
        page('graphql/queries-and-mutations/list-deliveries.md', 'List Deliveries'),
        page(
          'graphql/queries-and-mutations/get-delivery-estimates.md',
          'Get Delivery Estimates'
        ),
        page(
          'graphql/queries-and-mutations/get-delivery-derived-estimates-calculate.md',
          'Get Delivery Derived Estimate Calculation [Beta]'
        ),
        page('graphql/queries-and-mutations/appendix.md', 'Appendix'),
        page(
          'graphql/queries-and-mutations/upsert-payload-data.md',
          'Create payload for future dispatch',
          { unlisted: true }
        ),
        page(
          'graphql/queries-and-mutations/payload-fits.md',
          'Check Whether Your Payload Fits',
          { unlisted: true }
        ),
      ],
    },
    {
      label: 'Parcels',
      pages: [
        page('graphql/queries-and-mutations/get-parcel-rates.md', 'Get Parcel Rates'),
        page(
          'graphql/queries-and-mutations/purchase-shipping-label.md',
          'Purchase a Shipping Label'
        ),
        page(
          'graphql/queries-and-mutations/refund-shipping-label.md',
          'Refund a Shipping Label'
        ),
      ],
    },
    {
      label: 'Webhooks, Errors, & Statuses',
      pages: [
        page('graphql/webhooks-errors-statuses/webhooks.md', 'Webhooks'),
        page('graphql/webhooks-errors-statuses/errors.md', 'Errors'),
        page('graphql/webhooks-errors-statuses/statuses.md', 'Statuses'),
        page(
          'graphql/webhooks-errors-statuses/cancellation-reasons.md',
          'Cancellation Reasons'
        ),
      ],
    },
    {
      // Every page here is unlisted, so this group never renders in the nav.
      // It exists so the itinerary docs still have a home in the tree, and so
      // `itinerary-appendix` — one of the most actively edited files in the
      // whole set — is findable by someone reading this manifest.
      label: 'Itineraries',
      pages: [
        page('graphql/itineraries/itineraries-model.md', 'Introduction', {
          unlisted: true, hideTitle: true }),
        page(
          'graphql/itineraries/queries-and-mutations/overview.md',
          'Overview',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/requesting-quotes.md',
          'Requesting Quotes',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/upsert-examples.md',
          'Upsert Itinerary Examples',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/book-itinerary.md',
          'Book Itinerary',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/get-itinerary.md',
          'Get Itinerary',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/cancel-itinerary.md',
          'Cancel an Itinerary',
          { unlisted: true, hideTitle: true }
        ),
        page(
          'graphql/itineraries/queries-and-mutations/itinerary-appendix.md',
          'Itinerary Appendix',
          { unlisted: true }
        ),
        page(
          'graphql/itineraries/statuses-errors/itinerary-statuses.md',
          'Itinerary Statuses',
          { unlisted: true, hideTitle: true }
        ),
      ],
    },
  ],
}

// Webhook documentation, written for the portal rather than ported from
// docs.curri.com (see content/README.md). Content is verified against the
// sending code rather than against the old site: delivery webhooks against
// packages/curri-delivery-webhooks/src/curri.ts, payload webhooks against
// services/curri-integrations-service/src/webhooks/**.
//
// One shared group, then one group per webhook type. Adding a type is a new
// group with its setup and reference pages (see the registry in
// src/routes/webhooks/types.ts for the dashboard side of the same seam).
const WEBHOOKS: DocTree = {
  id: 'webhooks',
  label: 'Webhooks',
  basePath: '/docs/webhooks',
  groups: [
    {
      label: 'Webhooks',
      pages: [
        // The index is the quick start — it opens with its own h1 ("Getting
        // Started with Webhooks"), so the shell's title is suppressed.
        page('webhooks/getting-started.md', 'Getting started', {
          slug: '',
          hideTitle: true,
        }),
        page('webhooks/overview.md', 'How webhooks work'),
        page('webhooks/security.md', 'Authentication & signing'),
        page('webhooks/retries-and-failures.md', 'Retries & failures'),
      ],
    },
    {
      label: 'Delivery webhooks',
      pages: [
        page('webhooks/deliveries/setup.md', 'Set up delivery webhooks'),
        page('webhooks/delivery-statuses.md', 'Delivery lifecycle'),
        // The file and slug stay `payload` so /docs/webhooks/payload keeps
        // resolving; only the title says which type's body it describes.
        page('webhooks/payload.md', 'Delivery webhook body'),
      ],
    },
    {
      label: 'Payload webhooks',
      pages: [
        page('webhooks/payloads/setup.md', 'Set up payload webhooks'),
        page('webhooks/payloads/events.md', 'Payload events reference'),
      ],
    },
  ],
}

// Eclipse ERP setup. Entirely unlisted: these pages are handed to a specific
// integrator, and their URLs are quoted inside ERP installs — so they must stay
// reachable forever without ever appearing in the portal nav.
const ECLIPSE: DocTree = {
  id: 'eclipse',
  label: 'Eclipse ERP',
  basePath: '/docs/eclipse',
  groups: [
    {
      label: 'Eclipse ERP Integration',
      pages: [
        page('eclipse/overview.md', 'Introduction', { unlisted: true, hideTitle: true }),
        page('eclipse/control-records.md', 'Control Records', { unlisted: true }),
        page('eclipse/activity-triggers.md', 'Activity Triggers', {
          unlisted: true,
        }),
        page('eclipse/screens-setup.md', 'Screens Setup', { unlisted: true }),
        page(
          'eclipse/user-defined-menu-view-options.md',
          'User Defined Menu and View Options',
          { unlisted: true }
        ),
        page('eclipse/faqs.md', 'FAQs', { unlisted: true }),
        page('eclipse/release-notes.md', 'Release Notes', { unlisted: true }),
      ],
    },
  ],
}

const DSP: DocTree = {
  id: 'dsp',
  label: 'Delivery Service Providers',
  basePath: '/docs/dsp',
  groups: [
    {
      label: 'Delivery Service Providers',
      pages: [
        page('dsp/getting-quotes.md', 'Getting DSP Quotes', { unlisted: true }),
      ],
    },
  ],
}

// Single sign-on setup. Entirely unlisted, like Eclipse and DSP: the guide is
// handed to a customer's identity admin, who reaches it by URL.
const SSO: DocTree = {
  id: 'sso',
  label: 'Single sign-on',
  basePath: '/docs/sso',
  groups: [
    {
      label: 'Single sign-on',
      pages: [
        page('sso/microsoft-entra-id.md', 'Microsoft Entra ID (Azure AD)', {
          unlisted: true,
          hideTitle: true,
        }),
        page('sso/okta.md', 'Okta', { unlisted: true, hideTitle: true }),
      ],
    },
  ],
}

export const DOC_TREES: readonly DocTree[] = [GRAPHQL, WEBHOOKS, ECLIPSE, DSP, SSO]

export const treeById = (id: DocTreeId): DocTree => {
  const tree = DOC_TREES.find(candidate => candidate.id === id)
  if (!tree) throw new Error(`Unknown doc tree: ${id}`)
  return tree
}

export const pagesOf = (tree: DocTree): DocPage[] =>
  tree.groups.flatMap(group => group.pages)

/** Full portal path for a page, e.g. `/docs/graphql/queries-and-mutations/appendix`. */
export const pathOf = (tree: DocTree, page: DocPage): string =>
  page.slug ? `${tree.basePath}/${page.slug}` : tree.basePath

/**
 * Resolves a portal pathname to a page. Returns null for anything outside a doc
 * tree, so the shell can fall through to its normal routing.
 */
export const resolveDocPath = (
  pathname: string
): { tree: DocTree; page: DocPage } | null => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

  for (const tree of DOC_TREES) {
    if (path !== tree.basePath && !path.startsWith(`${tree.basePath}/`)) continue

    const slug = path.slice(tree.basePath.length).replace(/^\//, '')
    const found = pagesOf(tree).find(candidate => candidate.slug === slug)
    if (found) return { tree, page: found }

    // A path inside a tree that names no page is still a docs URL. Answering
    // with the tree index beats falling through to the landing page, which is
    // what the shell's default would otherwise do with a mistyped deep link.
    const index = pagesOf(tree).find(candidate => candidate.slug === '')
    if (index) return { tree, page: index }
  }

  return null
}
